<template>
  <AppLayout>
    <div class="site-page">
      <div class="page-header">
        <h1>Site / Chantier</h1>
      </div>

      <BaseCard class="filters-card">
        <div class="filters-grid">
          <BaseSelect v-model="selectedProjetId" label="Projet">
            <option value="">Tous les projets</option>
            <option v-for="projet in projets" :key="projet.id" :value="String(projet.id)">
              {{ projet.nom }} ({{ projet.code }})
            </option>
          </BaseSelect>

          <BaseSelect v-model="selectedSiteId" label="Site" :disabled="!selectedProjetId">
            <option value="">{{ selectedProjetId ? 'Tous les sites' : '-- Choisir un projet --' }}</option>
            <option v-for="site in filteredSites" :key="site.id" :value="String(site.id)">
              {{ site.nom }}
            </option>
          </BaseSelect>

          <BaseInput
            v-model="selectedDate"
            type="date"
            label="Filtre date"
          />
        </div>
      </BaseCard>

      <div class="page-tabs">
        <button class="page-tab" :class="{ active: activeTab === 'flux' }" @click="activeTab = 'flux'">Flux quantités</button>
        <button class="page-tab" :class="{ active: activeTab === 'receptions' }" @click="activeTab = 'receptions'">Réceptions</button>
        <button class="page-tab" :class="{ active: activeTab === 'poses' }" @click="activeTab = 'poses'">Poses</button>
        <div class="tab-actions">
          <BaseButton v-if="activeTab === 'receptions'" variant="primary" size="sm" @click="openReceptionModal">
            + Nouvelle réception
          </BaseButton>
          <BaseButton v-if="activeTab === 'poses'" variant="primary" size="sm" @click="openInstallationModal">
            + Nouvelle pose
          </BaseButton>
        </div>
      </div>

      <div v-if="error" class="error">{{ error }}</div>

      <section v-show="activeTab === 'flux'" class="panel-section">
        <div class="section-header">
          <h2>Flux quantités (site)</h2>
        </div>
        <BaseTable
          :columns="fluxColumns"
          :items="fluxRows"
          :loading="loadingFlux"
          :show-actions="false"
        />
      </section>

      <section v-show="activeTab === 'receptions'" class="panel-section">
        <div class="section-header">
          <h2>Réceptions</h2>
        </div>
        <BaseTable
          :columns="receptionColumns"
          :items="receptionsPaged"
          :loading="loadingReceptions"
          :show-actions="true"
          @view="viewReception"
        >
          <template #cell-dateReception="{ value }">
            {{ formatDate(value) }}
          </template>
          <template #cell-statut="{ value }">
            <BaseBadge :variant="getReceptionStatutVariant(value)">{{ value }}</BaseBadge>
          </template>
          <template #actions="{ item }">
            <BaseButton variant="ghost" size="sm" @click="viewReception(item)">Voir</BaseButton>
          </template>
        </BaseTable>
        <div v-if="receptionsTotalPages > 1" class="pagination-bar">
          <button class="pag-btn" :disabled="pageReceptions <= 1" @click="pageReceptions--">‹</button>
          <span class="pag-info">{{ pageReceptions }} / {{ receptionsTotalPages }}</span>
          <button class="pag-btn" :disabled="pageReceptions >= receptionsTotalPages" @click="pageReceptions++">›</button>
        </div>
      </section>

      <section v-show="activeTab === 'poses'" class="panel-section">
        <div class="section-header">
          <h2>Poses</h2>
        </div>
        <BaseTable
          :columns="installationColumns"
          :items="posesPaged"
          :loading="loadingInstallations"
          :show-actions="true"
        >
          <template #cell-dateInstallation="{ value }">
            {{ formatDate(value) }}
          </template>
          <template #cell-typeElement="{ item }">
            {{ resolveTypeLabel(item.typeElementPrefabriqueId) }}
          </template>
          <template #actions="{ item }">
            <BaseButton variant="ghost" size="sm" @click="openEditInstallationModal(item)">Modifier</BaseButton>
            <BaseButton variant="ghost" size="sm" @click="confirmDeleteInstallation(item)">Supprimer</BaseButton>
          </template>
        </BaseTable>
        <div v-if="posesTotalPages > 1" class="pagination-bar">
          <button class="pag-btn" :disabled="pagePoses <= 1" @click="pagePoses--">‹</button>
          <span class="pag-info">{{ pagePoses }} / {{ posesTotalPages }}</span>
          <button class="pag-btn" :disabled="pagePoses >= posesTotalPages" @click="pagePoses++">›</button>
        </div>
      </section>
    </div>

    <FormPage v-if="showReceptionModal" :wide="true" @close="closeReceptionModal">
      <template #title>Nouvelle réception</template>
      <form class="modal-form" @submit.prevent="saveReception">
        <div class="reception-form-grid">
          <BaseSelect v-model="receptionForm.projetId" label="Projet *" required>
            <option value="">-- Sélectionner un projet --</option>
            <option v-for="projet in projets" :key="projet.id" :value="String(projet.id)">
              {{ projet.nom }} ({{ projet.code }})
            </option>
          </BaseSelect>

          <BaseSelect v-model="receptionForm.siteId" label="Site *" required :disabled="!receptionForm.projetId">
            <option value="">{{ receptionForm.projetId ? '-- Sélectionner un site --' : '-- Choisir un projet --' }}</option>
            <option v-for="site in receptionFormSites" :key="site.id" :value="String(site.id)">
              {{ site.nom }}
            </option>
          </BaseSelect>

          <BaseSelect v-model="receptionForm.expeditionId" label="Expédition *" required :disabled="!receptionForm.siteId">
            <option value="">{{ receptionForm.siteId ? '-- Sélectionner --' : '-- Choisir un site --' }}</option>
            <option v-for="expedition in receptionFormExpeditions" :key="expedition.id" :value="String(expedition.id)">
              #{{ expedition.id }} - {{ formatDate(expedition.dateExpedition) }} ({{ expedition.statut }})
            </option>
          </BaseSelect>

          <BaseInput v-model="receptionForm.dateReception" label="Date réception *" type="date" />

          <BaseSelect v-model="receptionForm.statut" label="Statut">
            <option value="OK">OK</option>
            <option value="AVEC_DOMMAGES">Avec dommages</option>
            <option value="PARTIEL">Partiel</option>
          </BaseSelect>
        </div>

        <div v-if="receptionItems.length > 0 && receptionItems[0].typeElementPrefabriqueId" class="reception-items-section">
          <div class="reception-items-header">
            <h3>Éléments à réceptionner</h3>
            <span class="reception-items-count">{{ receptionItems.filter(i => i.typeElementPrefabriqueId).length }} élément(s)</span>
          </div>
          <div class="reception-items-table-wrap">
            <table class="reception-items-table">
              <thead>
                <tr>
                  <th class="col-code">Code</th>
                  <th class="col-desig">Désignation</th>
                  <th class="col-num">Envoyée</th>
                  <th class="col-num">Reçue</th>
                  <th class="col-num">Endommagée</th>
                  <th class="col-num">Manquante</th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="(item, index) in receptionItems"
                  :key="item.uid"
                  :class="{ 'row-alt': index % 2 === 1 }"
                >
                  <td class="col-code cell-mono">{{ resolveTypeCode(Number(item.typeElementPrefabriqueId)) }}</td>
                  <td class="col-desig">{{ resolveTypeDesignation(Number(item.typeElementPrefabriqueId)) }}</td>
                  <td class="col-num">
                    <input type="number" min="0" v-model="item.quantiteEnvoyee" class="qty-input" />
                  </td>
                  <td class="col-num">
                    <input type="number" min="0" v-model="item.quantiteRecue" class="qty-input" />
                  </td>
                  <td class="col-num">
                    <input type="number" min="0" v-model="item.quantiteEndommagee" class="qty-input" />
                  </td>
                  <td class="col-num">
                    <input type="number" min="0" v-model="item.quantiteManquante" class="qty-input" />
                  </td>
                </tr>
              </tbody>
              <tfoot>
                <tr class="row-total">
                  <td colspan="2">TOTAL</td>
                  <td class="col-num">{{ receptionItems.reduce((s, i) => s + (Number(i.quantiteEnvoyee) || 0), 0) }}</td>
                  <td class="col-num">{{ receptionItems.reduce((s, i) => s + (Number(i.quantiteRecue) || 0), 0) }}</td>
                  <td class="col-num">{{ receptionItems.reduce((s, i) => s + (Number(i.quantiteEndommagee) || 0), 0) }}</td>
                  <td class="col-num">{{ receptionItems.reduce((s, i) => s + (Number(i.quantiteManquante) || 0), 0) }}</td>
                </tr>
              </tfoot>
            </table>
          </div>
        </div>
        <p v-else-if="receptionForm.expeditionId" class="empty-hint">
          Sélectionnez une expédition pour charger les éléments.
        </p>

        <div v-if="receptionFormError" class="form-error">{{ receptionFormError }}</div>
        <div class="form-actions">
          <BaseButton type="button" variant="ghost" @click="closeReceptionModal">Annuler</BaseButton>
          <BaseButton type="submit" variant="primary" :loading="savingReception">Enregistrer la réception</BaseButton>
        </div>
      </form>
    </FormPage>

    <FormPage v-if="showInstallationModal" @close="closeInstallationModal">
      <template #title>{{ editingInstallation ? 'Modifier la pose' : 'Nouvelle pose' }}</template>
      <form class="modal-form" @submit.prevent="saveInstallation">
        <BaseSelect v-model="installationForm.projetId" label="Projet *" required>
          <option value="">-- Sélectionner un projet --</option>
          <option v-for="projet in projets" :key="projet.id" :value="String(projet.id)">
            {{ projet.nom }} ({{ projet.code }})
          </option>
        </BaseSelect>

        <BaseSelect v-model="installationForm.siteId" label="Site *" required :disabled="!installationForm.projetId">
          <option value="">{{ installationForm.projetId ? '-- Sélectionner un site --' : '-- Choisir un projet --' }}</option>
          <option v-for="site in installFormSites" :key="site.id" :value="String(site.id)">
            {{ site.nom }}
          </option>
        </BaseSelect>

        <div v-if="installFormStock.length > 0" class="stock-site-section">
          <h3>Stock disponible sur site</h3>
          <table class="stock-table">
            <thead>
              <tr>
                <th>Type</th>
                <th>Désignation</th>
                <th>Stock</th>
                <th>Quantité posée</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="stockItem in installFormStock" :key="stockItem.typeElementPrefabriqueId">
                <td>{{ stockItem.typeCode }}</td>
                <td>{{ stockItem.typeLibelle }}</td>
                <td>{{ stockItem.quantiteDisponible }}</td>
                <td>
                  <input
                    type="number"
                    min="0"
                    :max="stockItem.quantiteDisponible"
                    :value="installFormQuantites[stockItem.typeElementPrefabriqueId] ?? 0"
                    @input="setInstallQuantite(stockItem.typeElementPrefabriqueId, ($event.target as HTMLInputElement).value)"
                    class="stock-qty-input"
                  />
                </td>
              </tr>
            </tbody>
          </table>
        </div>
        <p v-else-if="installationForm.siteId && !loadingInstallFormStock" class="empty-hint">
          Aucun stock disponible sur ce site.
        </p>

        <BaseInput
          v-model="installationForm.dateInstallation"
          label="Date pose *"
          type="date"
        />

        <div v-if="installationFormError" class="form-error">{{ installationFormError }}</div>
        <div class="form-actions">
          <BaseButton type="button" variant="ghost" @click="closeInstallationModal">Annuler</BaseButton>
          <BaseButton type="submit" variant="primary" :loading="savingInstallation">Enregistrer</BaseButton>
        </div>
      </form>
    </FormPage>

    <FormPage v-if="showDeleteInstallationModal" @close="showDeleteInstallationModal = false">
      <template #title>Confirmer la suppression</template>
      <div class="modal-form">
        <p>Supprimer cette pose ?</p>
        <div class="form-actions">
          <BaseButton variant="ghost" @click="showDeleteInstallationModal = false">Annuler</BaseButton>
          <BaseButton variant="danger" :loading="deletingInstallation" @click="handleDeleteInstallation">Supprimer</BaseButton>
        </div>
      </div>
    </FormPage>
  </AppLayout>
</template>

<script setup lang="ts">
import { computed, onMounted, reactive, ref, watch } from 'vue';
import { useRouter } from 'vue-router';
import AppLayout from '../components/AppLayout.vue';
import BaseBadge from '../components/BaseBadge.vue';
import BaseButton from '../components/BaseButton.vue';
import BaseCard from '../components/BaseCard.vue';
import BaseInput from '../components/BaseInput.vue';
import BaseSelect from '../components/BaseSelect.vue';
import BaseTable, { type TableColumn } from '../components/BaseTable.vue';
import FormPage from '../components/FormPage.vue';
import { apiFetch } from '../utils/api';

interface Projet {
  id: number;
  nom: string;
  code: string;
}

interface Site {
  id: number;
  nom: string;
  projetId: number;
}

interface TypeElementPrefabrique {
  id: number;
  code: string;
  libelle: string;
}

interface Expedition {
  id: number;
  dateExpedition: string;
  statut: string;
}

interface Reception {
  id: number;
  expeditionId: number;
  siteId: number;
  siteNom?: string;
  projetId?: number;
  projetNom?: string;
  projetCode?: string;
  dateReception: string;
  statut: 'OK' | 'AVEC_DOMMAGES' | 'PARTIEL';
}

interface Installation {
  id: number;
  siteId: number;
  siteNom?: string;
  projetId?: number;
  projetNom?: string;
  projetCode?: string;
  typeElementPrefabriqueId: number;
  quantiteInstallee: number;
  dateInstallation: string;
}

interface FluxElement {
  typeElementPrefabriqueId: number;
  type: string;
  designation: string;
  quantiteRecue: number;
  quantiteEndommagee: number;
  quantiteManquante: number;
  quantiteStockSite: number;
  quantitePosee: number;
}

interface ReceptionItemDraft {
  uid: number;
  typeElementPrefabriqueId: string;
  quantiteEnvoyee: string;
  quantiteRecue: string;
  quantiteEndommagee: string;
  quantiteManquante: string;
}

const router = useRouter();

const projets = ref<Projet[]>([]);
const sites = ref<Site[]>([]);
const typesElements = ref<TypeElementPrefabrique[]>([]);
const receptions = ref<Reception[]>([]);
const installations = ref<Installation[]>([]);
const fluxElements = ref<FluxElement[]>([]);

const activeTab = ref('flux');

const selectedProjetId = ref('');
const selectedSiteId = ref('');
const selectedDate = ref('');

const loadingReceptions = ref(false);
const loadingInstallations = ref(false);
const loadingFlux = ref(false);
const error = ref<string | null>(null);

const showReceptionModal = ref(false);
const savingReception = ref(false);
const receptionFormError = ref<string | null>(null);

const showInstallationModal = ref(false);
const savingInstallation = ref(false);
const installationFormError = ref<string | null>(null);
const editingInstallation = ref<Installation | null>(null);
const showDeleteInstallationModal = ref(false);
const deletingInstallation = ref(false);
const deletingInstallationId = ref(0);

const receptionForm = reactive({
  projetId: '',
  siteId: '',
  expeditionId: '',
  dateReception: toDateInput(new Date()),
  statut: 'OK'
});

const receptionFormSites = ref<Site[]>([]);
const receptionFormExpeditions = ref<Expedition[]>([]);

const receptionItems = ref<ReceptionItemDraft[]>([
  {
    uid: 1,
    typeElementPrefabriqueId: '',
    quantiteEnvoyee: '',
    quantiteRecue: '',
    quantiteEndommagee: '0',
    quantiteManquante: '0'
  }
]);
const nextReceptionItemUid = ref(2);

const installationForm = reactive({
  projetId: '',
  siteId: '',
  dateInstallation: toDateInput(new Date())
});

interface StockSiteItem {
  typeElementPrefabriqueId: number;
  typeCode: string;
  typeLibelle: string;
  quantiteDisponible: number;
}
const installFormSites = ref<Site[]>([]);
const installFormStock = ref<StockSiteItem[]>([]);
const loadingInstallFormStock = ref(false);
const installFormQuantites = ref<Record<number, number>>({});

const fluxColumns: TableColumn[] = [
  { key: 'type', label: 'Type' },
  { key: 'designation', label: 'Désignation' },
  { key: 'quantiteRecue', label: 'Reçue' },
  { key: 'quantiteEndommagee', label: 'Endommagée' },
  { key: 'quantiteManquante', label: 'Manquante' },
  { key: 'quantiteStockSite', label: 'Stock site' },
  { key: 'quantitePosee', label: 'Posée' }
];

const receptionColumns: TableColumn[] = [
  { key: 'dateReception', label: 'Date' },
  { key: 'projetNom', label: 'Projet' },
  { key: 'siteNom', label: 'Site' },
  { key: 'expeditionId', label: 'Expédition' },
  { key: 'statut', label: 'Statut' }
];

const installationColumns: TableColumn[] = [
  { key: 'dateInstallation', label: 'Date' },
  { key: 'projetNom', label: 'Projet' },
  { key: 'siteNom', label: 'Site' },
  { key: 'typeElement', label: 'Type' },
  { key: 'quantiteInstallee', label: 'Qté posée' }
];

const filteredSites = computed(() => {
  const projetId = toNumberOrNull(selectedProjetId.value);
  const scoped = projetId === null ? sites.value : sites.value.filter((site) => site.projetId === projetId);
  return [...scoped].sort((a, b) => a.nom.localeCompare(b.nom, 'fr'));
});


const receptionsFiltered = computed(() => {
  const projetId = toNumberOrNull(selectedProjetId.value);
  const siteId = toNumberOrNull(selectedSiteId.value);

  return receptions.value.filter((reception) => {
    if (projetId !== null && Number(reception.projetId) !== projetId) {
      return false;
    }
    if (siteId !== null && Number(reception.siteId) !== siteId) {
      return false;
    }
    if (selectedDate.value && reception.dateReception !== selectedDate.value) {
      return false;
    }
    return true;
  });
});

const installationsFiltered = computed(() => {
  const projetId = toNumberOrNull(selectedProjetId.value);
  const siteId = toNumberOrNull(selectedSiteId.value);

  return installations.value.filter((installation) => {
    if (projetId !== null && Number(installation.projetId) !== projetId) {
      return false;
    }
    if (siteId !== null && Number(installation.siteId) !== siteId) {
      return false;
    }
    if (selectedDate.value && installation.dateInstallation !== selectedDate.value) {
      return false;
    }
    return true;
  });
});

const fluxRows = computed(() => fluxElements.value);

// ── Pagination ────────────────────────────────────────────────────────────────
const PAGE_SIZE = 10;

const pageReceptions  = ref(1);
const pagePoses       = ref(1);

watch([selectedProjetId, selectedSiteId, selectedDate], () => {
  pageReceptions.value = 1;
  pagePoses.value      = 1;
});

const receptionsSorted     = computed(() => [...receptionsFiltered.value].sort((a, b) => b.id - a.id));
const receptionsPaged      = computed(() => receptionsSorted.value.slice((pageReceptions.value - 1) * PAGE_SIZE, pageReceptions.value * PAGE_SIZE));
const receptionsTotalPages = computed(() => Math.max(1, Math.ceil(receptionsSorted.value.length / PAGE_SIZE)));

const posesSorted     = computed(() => [...installationsFiltered.value].sort((a, b) => b.id - a.id));
const posesPaged      = computed(() => posesSorted.value.slice((pagePoses.value - 1) * PAGE_SIZE, pagePoses.value * PAGE_SIZE));
const posesTotalPages = computed(() => Math.max(1, Math.ceil(posesSorted.value.length / PAGE_SIZE)));
// ──────────────────────────────────────────────────────────────────────────────


const kpis = computed(() => {
  const recu = fluxRows.value.reduce((sum, row) => sum + row.quantiteRecue, 0);
  const stockSite = fluxRows.value.reduce((sum, row) => sum + row.quantiteStockSite, 0);
  const pose = fluxRows.value.reduce((sum, row) => sum + row.quantitePosee, 0);
  const tauxPose = recu > 0 ? Math.round((pose / recu) * 100) : 0;
  return { recu, stockSite, pose, tauxPose };
});

watch(
  () => selectedProjetId.value,
  async () => {
    selectedSiteId.value = '';
    const projetId = toNumberOrNull(selectedProjetId.value);
    if (projetId !== null) {
      await loadSitesForProjet(projetId);
      const projetSites = sites.value.filter(s => s.projetId === projetId);
      if (projetSites.length === 1) {
        selectedSiteId.value = String(projetSites[0].id);
      }
    }
  }
);

watch(
  () => [selectedProjetId.value, selectedSiteId.value],
  () => {
    loadFluxData();
  }
);

// Watchers pour le formulaire réception (projet → sites, site → expéditions)
watch(
  () => receptionForm.projetId,
  async (newVal) => {
    receptionForm.siteId = '';
    receptionForm.expeditionId = '';
    receptionFormExpeditions.value = [];
    const projetId = toNumberOrNull(newVal);
    if (projetId !== null) {
      try {
        const res = await apiFetch(`/projets/${projetId}/sites`);
        receptionFormSites.value = ((res as { items: Site[] }).items || []);
      } catch {
        receptionFormSites.value = [];
      }
    } else {
      receptionFormSites.value = [];
    }
  }
);

watch(
  () => receptionForm.siteId,
  async (newVal) => {
    receptionForm.expeditionId = '';
    const siteId = toNumberOrNull(newVal);
    if (siteId !== null) {
      try {
        const response = await apiFetch(`/sites/${siteId}/expeditions`);
        const list = (response as { items: Expedition[] }).items || [];
        receptionFormExpeditions.value = list.filter((e) => e.statut !== 'ANNULE');
      } catch {
        receptionFormExpeditions.value = [];
      }
    } else {
      receptionFormExpeditions.value = [];
    }
  }
);

// Charger les items de l'expédition sélectionnée → pré-remplir les lignes de réception
watch(
  () => receptionForm.expeditionId,
  async (newVal) => {
    const expeditionId = toNumberOrNull(newVal);
    if (expeditionId === null) {
      receptionItems.value = [{ uid: 1, typeElementPrefabriqueId: '', quantiteEnvoyee: '', quantiteRecue: '', quantiteEndommagee: '0', quantiteManquante: '0' }];
      nextReceptionItemUid.value = 2;
      return;
    }
    try {
      const res = await apiFetch(`/expeditions/${expeditionId}/items`);
      const items = (res as { items: Array<{ typeElementPrefabriqueId: number; quantiteEnvoyee: number }> }).items || [];
      if (items.length > 0) {
        let uid = 1;
        receptionItems.value = items.map(item => ({
          uid: uid++,
          typeElementPrefabriqueId: String(item.typeElementPrefabriqueId),
          quantiteEnvoyee: String(item.quantiteEnvoyee),
          quantiteRecue: String(item.quantiteEnvoyee),
          quantiteEndommagee: '0',
          quantiteManquante: '0'
        }));
        nextReceptionItemUid.value = uid;
      }
    } catch {
      // non-bloquant
    }
  }
);

// Watchers pour le formulaire pose (projet → sites, site → stock)
watch(
  () => installationForm.projetId,
  async (newVal) => {
    installationForm.siteId = '';
    installFormStock.value = [];
    installFormQuantites.value = {};
    const projetId = toNumberOrNull(newVal);
    if (projetId !== null) {
      try {
        const res = await apiFetch(`/projets/${projetId}/sites`);
        installFormSites.value = ((res as { items: Site[] }).items || []);
      } catch {
        installFormSites.value = [];
      }
    } else {
      installFormSites.value = [];
    }
  }
);

watch(
  () => installationForm.siteId,
  async (newVal) => {
    installFormStock.value = [];
    installFormQuantites.value = {};
    const siteId = toNumberOrNull(newVal);
    if (siteId !== null) {
      loadingInstallFormStock.value = true;
      try {
        const res = await apiFetch(`/installations/sites/${siteId}/stock-site`);
        const items = (res as { items: Array<{ typeElementPrefabriqueId: number; typeCode: string; typeLibelle: string; quantiteDisponible: number }> }).items || [];
        installFormStock.value = items;
      } catch {
        installFormStock.value = [];
      } finally {
        loadingInstallFormStock.value = false;
      }
    }
  }
);

function setInstallQuantite(typeId: number, value: string) {
  const qty = Number(value);
  if (Number.isFinite(qty) && qty >= 0) {
    installFormQuantites.value[typeId] = qty;
  } else {
    delete installFormQuantites.value[typeId];
  }
}

onMounted(async () => {
  await loadProjets();
  // Auto-sélection si un seul projet
  if (projets.value.length === 1 && !selectedProjetId.value) {
    selectedProjetId.value = String(projets.value[0].id);
  }
  await Promise.all([loadTypesElements(), loadReceptions(), loadInstallations()]);
  await loadFluxData();
});

async function loadProjets() {
  try {
    const response = await apiFetch('/projets');
    projets.value = (response as { items: Projet[] }).items || [];
  } catch (err) {
    error.value = err instanceof Error ? err.message : 'Erreur lors du chargement des projets';
  }
}

async function loadSitesForProjet(projetId: number) {
  try {
    const res = await apiFetch(`/projets/${projetId}/sites`);
    const items = (res as { items: Array<{ id: number; nom: string }> }).items || [];
    const newSites = items.map((site) => ({
      id: site.id,
      nom: site.nom,
      projetId
    })) satisfies Site[];
    sites.value = [
      ...sites.value.filter((s) => s.projetId !== projetId),
      ...newSites
    ];
  } catch (err) {
    console.error('Erreur lors du chargement des sites:', err);
  }
}

async function loadTypesElements() {
  try {
    const response = await apiFetch('/types-elements-prefabriques');
    typesElements.value = (response as { items: TypeElementPrefabrique[] }).items || [];
  } catch (err) {
    console.error('Erreur lors du chargement des types:', err);
  }
}

async function loadReceptions() {
  loadingReceptions.value = true;
  try {
    const response = await apiFetch('/receptions/receptions-site?limit=500&offset=0');
    receptions.value = (response as { items: Reception[] }).items || [];
  } catch (err) {
    error.value = err instanceof Error ? err.message : 'Erreur lors du chargement des réceptions';
  } finally {
    loadingReceptions.value = false;
  }
}

async function loadInstallations() {
  loadingInstallations.value = true;
  try {
    const response = await apiFetch('/installations/installations-site?limit=500&offset=0');
    installations.value = (response as { items: Installation[] }).items || [];
  } catch (err) {
    error.value = err instanceof Error ? err.message : 'Erreur lors du chargement des poses';
  } finally {
    loadingInstallations.value = false;
  }
}

async function loadFluxData() {
  const projetId = toNumberOrNull(selectedProjetId.value);
  const siteId = toNumberOrNull(selectedSiteId.value);
  const projetIds = projetId !== null ? [projetId] : projets.value.map((p) => p.id);

  if (projetIds.length === 0) {
    fluxElements.value = [];
    return;
  }

  loadingFlux.value = true;
  try {
    const params = new URLSearchParams();
    if (siteId !== null) params.set('siteId', String(siteId));
    const qs = params.toString() ? `?${params.toString()}` : '';

    const results = await Promise.all(
      projetIds.map((pid) =>
        apiFetch(`/metriques/projets/${pid}/flux-elements${qs}`).catch(() => ({ items: [] }))
      )
    );

    const mergedMap = new Map<number, FluxElement>();
    results.forEach((res) => {
      ((res as { items: FluxElement[] }).items || []).forEach((item) => {
        const existing = mergedMap.get(item.typeElementPrefabriqueId);
        if (existing) {
          existing.quantiteRecue += item.quantiteRecue;
          existing.quantiteEndommagee += item.quantiteEndommagee;
          existing.quantiteManquante += item.quantiteManquante;
          existing.quantiteStockSite += item.quantiteStockSite;
          existing.quantitePosee += item.quantitePosee;
        } else {
          mergedMap.set(item.typeElementPrefabriqueId, { ...item });
        }
      });
    });
    fluxElements.value = Array.from(mergedMap.values());
  } catch (err) {
    error.value = err instanceof Error ? err.message : 'Erreur lors du chargement du flux site';
    fluxElements.value = [];
  } finally {
    loadingFlux.value = false;
  }
}

function openReceptionModal() {
  receptionForm.projetId = '';
  receptionForm.siteId = '';
  receptionForm.expeditionId = '';
  receptionForm.dateReception = toDateInput(new Date());
  receptionForm.statut = 'OK';
  receptionFormSites.value = [];
  receptionFormExpeditions.value = [];
  receptionItems.value = [
    {
      uid: 1,
      typeElementPrefabriqueId: '',
      quantiteEnvoyee: '',
      quantiteRecue: '',
      quantiteEndommagee: '0',
      quantiteManquante: '0'
    }
  ];
  nextReceptionItemUid.value = 2;
  receptionFormError.value = null;
  showReceptionModal.value = true;
}

function closeReceptionModal() {
  showReceptionModal.value = false;
  receptionFormError.value = null;
}

function addReceptionItemRow() {
  receptionItems.value.push({
    uid: nextReceptionItemUid.value++,
    typeElementPrefabriqueId: '',
    quantiteEnvoyee: '',
    quantiteRecue: '',
    quantiteEndommagee: '0',
    quantiteManquante: '0'
  });
}

function removeReceptionItemRow(uid: number) {
  receptionItems.value = receptionItems.value.filter((item) => item.uid !== uid);
}

async function saveReception() {
  const expeditionId = toNumberOrNull(receptionForm.expeditionId);
  if (expeditionId === null) {
    receptionFormError.value = 'Veuillez sélectionner une expédition.';
    return;
  }
  if (!receptionForm.dateReception) {
    receptionFormError.value = 'La date de réception est requise.';
    return;
  }

  savingReception.value = true;
  receptionFormError.value = null;
  try {
    const created = await apiFetch('/receptions/receptions-site', {
      method: 'POST',
      body: {
        expeditionId,
        dateReception: receptionForm.dateReception,
        statut: receptionForm.statut
      }
    }) as { id: number };

    const validItems = receptionItems.value
      .map((item) => ({
        typeElementPrefabriqueId: toNumberOrNull(item.typeElementPrefabriqueId),
        quantiteEnvoyee: toNumberOrNull(item.quantiteEnvoyee),
        quantiteRecue: toNumberOrNull(item.quantiteRecue),
        quantiteEndommagee: toNumberOrNull(item.quantiteEndommagee),
        quantiteManquante: toNumberOrNull(item.quantiteManquante)
      }))
      .filter(
        (item): item is {
          typeElementPrefabriqueId: number;
          quantiteEnvoyee: number;
          quantiteRecue: number;
          quantiteEndommagee: number;
          quantiteManquante: number;
        } =>
          item.typeElementPrefabriqueId !== null &&
          item.quantiteEnvoyee !== null &&
          item.quantiteRecue !== null &&
          item.quantiteEndommagee !== null &&
          item.quantiteManquante !== null &&
          item.quantiteEnvoyee > 0
      );

    await Promise.all(
      validItems.map((item) =>
        apiFetch(`/receptions/receptions-site/${created.id}/items`, {
          method: 'POST',
          body: item
        })
      )
    );

    closeReceptionModal();
    await Promise.all([loadReceptions(), loadFluxData()]);
  } catch (err) {
    receptionFormError.value = err instanceof Error ? err.message : 'Erreur lors de la création de la réception';
  } finally {
    savingReception.value = false;
  }
}

function openInstallationModal() {
  editingInstallation.value = null;
  installationForm.projetId = '';
  installationForm.siteId = '';
  installationForm.dateInstallation = toDateInput(new Date());
  installFormSites.value = [];
  installFormStock.value = [];
  installFormQuantites.value = {};
  installationFormError.value = null;
  showInstallationModal.value = true;
}

function openEditInstallationModal(installation: Installation) {
  editingInstallation.value = installation;
  installationForm.projetId = String(installation.projetId);
  installationForm.siteId = String(installation.siteId);
  installationForm.dateInstallation = installation.dateInstallation;
  installFormQuantites.value = { [installation.typeElementPrefabriqueId]: installation.quantiteInstallee };
  installationFormError.value = null;
  showInstallationModal.value = true;
}

function closeInstallationModal() {
  showInstallationModal.value = false;
  editingInstallation.value = null;
  installationFormError.value = null;
}

async function saveInstallation() {
  const siteId = toNumberOrNull(installationForm.siteId);

  if (siteId === null) {
    installationFormError.value = 'Veuillez sélectionner un projet et un site.';
    return;
  }
  if (!installationForm.dateInstallation) {
    installationFormError.value = 'La date de pose est requise.';
    return;
  }

  // Collecter les items avec quantité > 0
  const itemsToSave = Object.entries(installFormQuantites.value)
    .map(([typeId, qty]) => ({ typeElementPrefabriqueId: Number(typeId), quantiteInstallee: qty }))
    .filter(item => item.quantiteInstallee > 0);

  if (itemsToSave.length === 0 && !editingInstallation.value) {
    installationFormError.value = 'Veuillez renseigner au moins une quantité posée.';
    return;
  }

  savingInstallation.value = true;
  installationFormError.value = null;
  try {
    if (editingInstallation.value) {
      const item = itemsToSave[0];
      if (item) {
        await apiFetch(`/installations/installations-site/${editingInstallation.value.id}`, {
          method: 'PATCH',
          body: { ...item, dateInstallation: installationForm.dateInstallation }
        });
      }
    } else {
      for (const item of itemsToSave) {
        await apiFetch(`/installations/sites/${siteId}/installations`, {
          method: 'POST',
          body: { ...item, dateInstallation: installationForm.dateInstallation }
        });
      }
    }

    closeInstallationModal();
    await Promise.all([loadInstallations(), loadFluxData()]);
  } catch (err) {
    installationFormError.value = err instanceof Error ? err.message : 'Erreur lors de l’enregistrement de la pose';
  } finally {
    savingInstallation.value = false;
  }
}

function confirmDeleteInstallation(installation: Installation) {
  deletingInstallationId.value = installation.id;
  showDeleteInstallationModal.value = true;
}

async function handleDeleteInstallation() {
  deletingInstallation.value = true;
  try {
    await apiFetch(`/installations/installations-site/${deletingInstallationId.value}`, {
      method: 'DELETE'
    });
    showDeleteInstallationModal.value = false;
    await Promise.all([loadInstallations(), loadFluxData()]);
  } catch (err) {
    error.value = err instanceof Error ? err.message : 'Erreur lors de la suppression';
  } finally {
    deletingInstallation.value = false;
  }
}

function viewReception(reception: Reception) {
  router.push({ name: 'reception-detail', params: { id: reception.id } });
}

function resolveTypeLabel(typeId: number): string {
  const found = typesElements.value.find((type) => Number(type.id) === typeId);
  return found ? `${found.libelle} (${found.code})` : `Type #${typeId}`;
}

function resolveTypeCode(typeId: number): string {
  const found = typesElements.value.find((type) => Number(type.id) === typeId);
  return found?.code ?? '-';
}

function resolveTypeDesignation(typeId: number): string {
  const found = typesElements.value.find((type) => Number(type.id) === typeId);
  return found?.libelle ?? '-';
}

function getReceptionStatutVariant(statut: Reception['statut']): 'default' | 'primary' | 'success' | 'warning' | 'error' {
  switch (statut) {
    case 'OK':
      return 'success';
    case 'AVEC_DOMMAGES':
      return 'warning';
    case 'PARTIEL':
      return 'primary';
    default:
      return 'default';
  }
}

function formatDate(iso: string) {
  return new Date(iso).toLocaleDateString('fr-FR');
}

function toNumberOrNull(value: string): number | null {
  if (value.trim() === '') {
    return null;
  }
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
}

function toDateInput(date: Date) {
  const year = date.getFullYear();
  const month = `${date.getMonth() + 1}`.padStart(2, '0');
  const day = `${date.getDate()}`.padStart(2, '0');
  return `${year}-${month}-${day}`;
}
</script>

<style scoped>
.site-page {
  max-width: 1400px;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  gap: var(--spacing-lg);
}

.page-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--spacing-md);
}

.page-header h1 {
  margin: 0;
}

.filters-card {
  padding: var(--spacing-md);
}

.page-tabs {
  display: flex;
  gap: 2px;
  flex-wrap: wrap;
  align-items: center;
  border-bottom: 2px solid var(--color-border-light);
  margin-bottom: var(--spacing-lg);
}

.page-tab {
  padding: var(--spacing-xs) var(--spacing-md);
  border: none;
  background: transparent;
  color: var(--color-text-secondary);
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-medium);
  cursor: pointer;
  border-bottom: 2px solid transparent;
  margin-bottom: -2px;
  border-radius: var(--radius-sm) var(--radius-sm) 0 0;
  transition: color 0.15s, border-color 0.15s;
  white-space: nowrap;
}

.page-tab:hover {
  color: var(--color-primary);
  background: var(--color-bg-secondary);
}

.page-tab.active {
  color: var(--color-primary);
  border-bottom-color: var(--color-primary);
  font-weight: var(--font-weight-semibold);
}

.tab-actions {
  margin-left: auto;
  padding-bottom: 2px;
}

.filters-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  gap: var(--spacing-md);
}

.pagination-bar {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: var(--spacing-sm);
  padding: var(--spacing-sm) var(--spacing-md);
  border-top: 1px solid var(--color-border-light);
  background: var(--color-bg-secondary);
  border-radius: 0 0 var(--radius-lg) var(--radius-lg);
}

.pag-btn {
  width: 32px;
  height: 32px;
  border: 1px solid var(--color-border-light);
  background: var(--color-bg-primary);
  border-radius: var(--radius-sm);
  cursor: pointer;
  font-size: var(--font-size-base);
  color: var(--color-text-primary);
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background 0.15s, border-color 0.15s;
}

.pag-btn:hover:not(:disabled) {
  background: var(--color-bg-secondary);
  border-color: var(--color-primary);
  color: var(--color-primary);
}

.pag-btn:disabled {
  opacity: 0.35;
  cursor: not-allowed;
}

.pag-info {
  font-size: var(--font-size-sm);
  color: var(--color-text-secondary);
  min-width: 60px;
  text-align: center;
}

.kpis-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(190px, 1fr));
  gap: var(--spacing-md);
}

.kpi-card {
  padding: var(--spacing-lg);
  text-align: center;
}

.kpi-value {
  font-size: var(--font-size-4xl);
  font-weight: var(--font-weight-bold);
  color: var(--color-primary);
}

.kpi-label {
  margin-top: var(--spacing-xs);
  color: var(--color-text-secondary);
  font-size: var(--font-size-sm);
}

.panel-section {
  display: flex;
  flex-direction: column;
  gap: var(--spacing-sm);
}

.section-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.section-header h2 {
  margin: 0;
}

.modal-form {
  display: flex;
  flex-direction: column;
  gap: var(--spacing-md);
}

.items-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.items-header h3 {
  margin: 0;
  font-size: var(--font-size-lg);
}

.reception-item-row {
  display: grid;
  grid-template-columns: 2fr 1fr 1fr 1fr 1fr auto;
  gap: var(--spacing-sm);
  align-items: end;
}

.reception-form-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: var(--spacing-md);
}

.reception-items-section {
  margin-top: var(--spacing-lg);
  border: 1px solid var(--color-border-light);
  border-radius: var(--radius-md);
  overflow: hidden;
}

.reception-items-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: var(--spacing-sm) var(--spacing-md);
  background: var(--color-bg-secondary);
  border-bottom: 1px solid var(--color-border-light);
}

.reception-items-header h3 {
  margin: 0;
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-semibold);
}

.reception-items-count {
  font-size: var(--font-size-xs);
  color: var(--color-text-secondary);
}

.reception-items-table-wrap {
  overflow-x: auto;
}

.reception-items-table {
  width: 100%;
  border-collapse: collapse;
  font-size: var(--font-size-sm);
}

.reception-items-table th {
  padding: var(--spacing-xs) var(--spacing-sm);
  background: var(--color-bg-tertiary, #eef1f6);
  border-bottom: 1px solid var(--color-border-light);
  font-size: var(--font-size-xs);
  font-weight: var(--font-weight-semibold);
  text-transform: uppercase;
  letter-spacing: 0.3px;
  color: var(--color-text-secondary);
  white-space: nowrap;
}

.reception-items-table td {
  padding: var(--spacing-xs) var(--spacing-sm);
  border-bottom: 1px solid var(--color-border-light);
}

.col-code { width: 100px; }
.col-desig { min-width: 140px; }
.col-num { text-align: right; width: 100px; font-variant-numeric: tabular-nums; }
.cell-mono { font-family: var(--font-mono, monospace); font-size: var(--font-size-xs); }

.row-alt { background: var(--color-bg-secondary); }

.row-total {
  background: var(--color-bg-secondary);
  font-weight: var(--font-weight-semibold);
}

.row-total td {
  padding: var(--spacing-sm);
  border-top: 2px solid var(--color-border-light);
}

.qty-input {
  width: 80px;
  padding: var(--spacing-xs) var(--spacing-sm);
  border: 1px solid var(--color-border-medium);
  border-radius: var(--radius-md);
  font-size: var(--font-size-sm);
  text-align: right;
  font-variant-numeric: tabular-nums;
}

.qty-input:focus {
  outline: none;
  border-color: var(--color-primary);
  box-shadow: 0 0 0 2px rgba(30, 86, 217, 0.15);
}

.empty-hint { text-align: center; color: var(--color-text-secondary); padding: var(--spacing-lg); font-size: var(--font-size-sm); }

.form-error {
  padding: var(--spacing-sm);
  border: 1px solid #fcc;
  border-radius: var(--radius-md);
  background: #fee;
  color: #c33;
  font-size: var(--font-size-sm);
}

.form-actions {
  display: flex;
  justify-content: flex-end;
  gap: var(--spacing-sm);
}

.stock-site-section {
  margin: var(--spacing-md) 0;
}

.stock-site-section h3 {
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-semibold);
  margin-bottom: var(--spacing-sm);
}

.stock-table {
  width: 100%;
  border-collapse: collapse;
  font-size: var(--font-size-sm);
}

.stock-table th {
  text-align: left;
  padding: var(--spacing-xs) var(--spacing-sm);
  background: var(--color-bg-secondary);
  border-bottom: 1px solid var(--color-border-light);
  font-weight: var(--font-weight-semibold);
  font-size: var(--font-size-xs);
  text-transform: uppercase;
  color: var(--color-text-secondary);
}

.stock-table td {
  padding: var(--spacing-xs) var(--spacing-sm);
  border-bottom: 1px solid var(--color-border-light);
}

.stock-qty-input {
  width: 80px;
  padding: var(--spacing-xs) var(--spacing-sm);
  border: 1px solid var(--color-border-medium);
  border-radius: var(--radius-md);
  font-size: var(--font-size-sm);
}

.stock-qty-input:focus {
  outline: none;
  border-color: var(--color-border-focus);
  box-shadow: var(--shadow-focus);
}

.empty-hint {
  text-align: center;
  color: var(--color-text-secondary);
  padding: var(--spacing-lg);
  font-size: var(--font-size-sm);
}

.error {
  padding: var(--spacing-md);
  background: #fee;
  border: 1px solid #fcc;
  border-radius: var(--radius-md);
  color: #c33;
}

.stock-info {
  display: flex;
  align-items: center;
  gap: var(--spacing-sm);
  padding: var(--spacing-sm) var(--spacing-md);
  background: var(--color-bg-secondary);
  border-radius: var(--radius-md);
  font-size: var(--font-size-sm);
  color: var(--color-text-secondary);
}

.stock-info strong {
  color: var(--color-text-primary);
}

@media (max-width: 1100px) {
  .reception-item-row {
    grid-template-columns: 1fr;
  }
}
</style>
